import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { FormErrorProvider } from "src/contexts/form-error-context";
import { TraitType } from "./types";
import { lazyNamed } from "src/utils/lazy-named";

const CreateTrait = lazyNamed(() => import("./create-trait"), "CreateTrait");
const CreateTraitFromTemplate = lazyNamed(
  () => import("src/pages/traits/components/create-trait-from-template"),
  "CreateTraitFromTemplate",
);
const DuplicateTrait = lazyNamed(
  () => import("src/pages/traits/components/duplicate-trait"),
  "DuplicateTrait",
);
const Trait = lazyNamed(() => import("./trait"), "Trait");
const Layout = lazyNamed(() => import("./components/layout"), "Layout");
const traitsImport = () => import("./traits");
const TraitsTable = lazyNamed(traitsImport, "TraitsTable");

export const preloads: RoutePreload[] = [["/traits", traitsImport]];
const TraitConfiguration = lazyNamed(
  () => import("./trait/configuration"),
  "TraitConfiguration",
);
const TraitUsage = lazyNamed(() => import("./trait/usage-table"), "TraitUsage");
const TraitLineage = lazyNamed(() => import("./trait/lineage"), "TraitLineage");
const TraitSuggestions = lazyNamed(
  () => import("./trait/suggestions"),
  "TraitSuggestions",
);
const PredictionAnalysis = lazyNamed(
  () => import("./trait/prediction-analysis"),
  "PredictionAnalysis",
);
const PredictiveTraitsAdmin = lazyNamed(
  () => import("./admin"),
  "PredictiveTraitsAdmin",
);
const CreateDemoPredictor = lazyNamed(
  () => import("./admin/create-demo"),
  "CreateDemoPredictor",
);
const CreateDemoCatalogRecommender = lazyNamed(
  () => import("./admin/create-demo-catalog-rec"),
  "CreateDemoCatalogRecommender",
);
const CreateDemoLookalike = lazyNamed(
  () => import("./admin/create-demo-lookalike"),
  "CreateDemoLookalike",
);
const ManageJobs = lazyNamed(() => import("./admin/manage-jobs"), "ManageJobs");

export const routes: Array<RouteObject> = [
  {
    path: "traits",
    spacesAccess: "accessible-from-limited-view",
    element: <Layout />,
    children: [
      {
        index: true,
        spacesAccess: "accessible-from-limited-view",
        element: <Navigate to="active" />,
      },
      {
        path: "admin",
        spacesAccess: "hidden-from-limited-view",
        element: <PredictiveTraitsAdmin />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="create" />,
          },
          {
            path: "create",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateDemoPredictor />,
          },
          {
            path: "create-catalog-rec",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateDemoCatalogRecommender />,
          },
          {
            path: "create-lookalike",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateDemoLookalike />,
          },
          {
            path: "manage-jobs",
            spacesAccess: "hidden-from-limited-view",
            element: <ManageJobs />,
          },
        ],
      },
      {
        path: "active",
        spacesAccess: "accessible-from-limited-view",
        element: <TraitsTable type={TraitType.Active} />,
        children: [
          {
            path: "new",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FormErrorProvider>
                <CreateTrait isTemplate={false} />
              </FormErrorProvider>
            ),
          },
          {
            path: "new_from_template",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateTraitFromTemplate />,
          },
          {
            path: ":traitId",
            spacesAccess: "accessible-from-limited-view",
            element: <Trait type={TraitType.Active} />,
            children: [
              {
                index: true,
                spacesAccess: "accessible-from-limited-view",
                element: <Navigate to="configuration" />,
              },
              {
                path: "configuration",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitConfiguration />,
              },
              {
                path: "usage",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitUsage />,
              },
              {
                path: "suggestions",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitSuggestions />,
              },
              {
                path: "lineage",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitLineage />,
              },
              {
                path: "prediction-analysis",
                spacesAccess: "accessible-from-limited-view",
                element: <PredictionAnalysis />,
              },
            ],
          },
          {
            path: ":traitId/duplicate",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FormErrorProvider>
                <DuplicateTrait />
              </FormErrorProvider>
            ),
          },
        ],
      },
      {
        path: "templates",
        spacesAccess: "accessible-from-limited-view",
        element: <TraitsTable type={TraitType.Templates} />,
        children: [
          {
            path: "new",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FormErrorProvider>
                <CreateTrait isTemplate />
              </FormErrorProvider>
            ),
          },
          {
            path: ":traitId",
            spacesAccess: "accessible-from-limited-view",
            element: <Trait type={TraitType.Templates} />,
            children: [
              {
                index: true,
                spacesAccess: "accessible-from-limited-view",
                element: <Navigate to="configuration" />,
              },
              {
                path: "configuration",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitConfiguration />,
              },
              {
                path: "usage",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitUsage />,
              },
              {
                path: "suggestions",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitSuggestions />,
              },
              {
                path: "lineage",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitLineage />,
              },
            ],
          },
        ],
      },
      {
        path: "archived",
        spacesAccess: "accessible-from-limited-view",
        element: <TraitsTable type={TraitType.Archived} />,
        children: [
          {
            path: ":traitId",
            spacesAccess: "accessible-from-limited-view",
            element: <Trait type={TraitType.Archived} />,
            children: [
              {
                index: true,
                spacesAccess: "accessible-from-limited-view",
                element: <Navigate to="configuration" />,
              },
              {
                path: "configuration",
                spacesAccess: "accessible-from-limited-view",
                element: <TraitConfiguration />,
              },
            ],
          },
        ],
      },
    ],
  },
];
